import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// API Base URL - defaults to /api for same-origin requests
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ═══════════════════════════════════════════════════════
// Token accessor — set by AuthProvider after Keycloak init
// ═══════════════════════════════════════════════════════

let tokenAccessor: (() => string | undefined) | null = null;

export function setTokenAccessor(fn: () => string | undefined) {
  tokenAccessor = fn;
}

/** Get current Keycloak token (for direct fetch calls like blob downloads) */
export function getKeycloakToken(): string | undefined {
  return tokenAccessor?.();
}

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from Keycloak via accessor
    const token = tokenAccessor?.();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor — auto-refresh token on 401 and retry
let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token?: string) {
  for (const p of pendingRequests) {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token!);
    }
  }
  pendingRequests = [];
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt token refresh if a token was sent (user was logged in but token expired).
    // Skip refresh for requests that never had a token (public/unauthenticated requests).
    const hadToken = originalRequest?.headers?.Authorization;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      hadToken &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/')
    ) {
      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call BFF session endpoint to get a refreshed token
        const res = await fetch('/api/v1/auth/session', { credentials: 'include' });
        if (!res.ok) throw new Error('Session refresh failed');

        const data = await res.json();
        const newToken: string = data.data.accessToken;

        // Update the shared token accessor
        if (tokenAccessor) {
          // The accessor reads from AuthProvider's ref — we need to notify it
          window.dispatchEvent(
            new CustomEvent('auth:token-refreshed', { detail: { accessToken: newToken, expiresAt: data.data.expiresAt } })
          );
        }

        // Retry original request + all queued requests
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Redirect to login for non-embed pages
        if (!window.location.pathname.startsWith('/embed')) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Type-safe API methods
export const api = {
  get: <T>(url: string, config?: object) =>
    apiClient.get<T>(url, config).then(res => res.data),

  post: <T>(url: string, data?: object, config?: object) =>
    apiClient.post<T>(url, data, config).then(res => res.data),

  put: <T>(url: string, data?: object, config?: object) =>
    apiClient.put<T>(url, data, config).then(res => res.data),

  patch: <T>(url: string, data?: object, config?: object) =>
    apiClient.patch<T>(url, data, config).then(res => res.data),

  delete: <T>(url: string, config?: object) =>
    apiClient.delete<T>(url, config).then(res => res.data),
};

export default apiClient;
